import type { Finding } from './workflowFindings'
import type { GraphNode } from './workflowGraph'

/**
 * ADR-136 D5/D6 — the findings that are NOT validator errors.
 *
 * P2a/P2b surfaced what the canonical validator says, and everything it says
 * BLOCKS. But the two observations that mattered most in the specimen were
 * neither blocking nor produced by anything:
 *
 *   - `get_ticket` and `tag_matt` are typed `agent` for work that is mechanical
 *   - `build_mention_comment` hardcodes an id in a python literal
 *
 * The draft passes validation with both. So there was no producer for "valid,
 * and will bite you later" — which is exactly the severity the operator most
 * needs, because it is the class that ships.
 *
 * ## Why the offered fix is a prompt, not a transform
 *
 * Converting an `agent` step to `python` cannot be done blindly: someone has to
 * write the code. A transform that emitted a stub would produce a change the
 * operator cannot evaluate and the validator would accept.
 *
 * So the fix hands the finding to the ASSISTANT as a pre-filled request. That is
 * honest about who is doing the work, and it composes with what already exists:
 * the assistant can now emit a multi-file changeset (ADR-134 D1), it is grounded
 * in the real validation (ADR-136 D3), and its answer lands in the same review
 * path as any other edit. One click, a real proposal, nothing silent.
 */

/** Words that indicate a step is submitting something fixed, not deciding. */
const MECHANICAL = [
  'exactly as-is', 'exactly as is', 'verbatim', 'do not retype',
  'do not paraphrase', 'do not translate', 'as retrieved', 'without modification',
  'pre-built', 'prebuilt',
]

/** Words that indicate genuine judgement, which justifies an `agent` step. */
const JUDGEMENT = [
  'judge', 'decide', 'classify', 'evaluate', 'assess', 'review', 'determine',
  'unclear', 'ambiguous', 'draft ', 'summarize', 'summarise', 'interpret',
  'choose', 'rank', 'explain',
]

/** A long integer — Asana/HubSpot ids are 8+ digits. */
const LONG_ID = /\b\d{8,}\b/

function textOf(spec: unknown): string {
  if (typeof spec === 'string') return spec
  if (spec && typeof spec === 'object') {
    const o = spec as Record<string, unknown>
    return [o.run, o.code, o.command].filter(v => typeof v === 'string').join('\n')
  }
  return ''
}

/**
 * adviseAgentStep flags an `agent` step doing mechanical work.
 *
 * Conservative on purpose: it fires only when a mechanical marker is present AND
 * no judgement word is. A false positive here is worse than a miss — it teaches
 * the operator that the badges are noise, and once they believe that, the
 * blocking findings stop being read too.
 */
export function adviseAgentStep(node: GraphNode): Finding | null {
  if (node.taskType !== 'agent') return null
  const text = textOf(node.spec).toLowerCase()
  if (!text) return null
  if (JUDGEMENT.some(w => text.includes(w))) return null
  if (!MECHANICAL.some(w => text.includes(w))) return null
  return {
    nodeId: node.id,
    severity: 'warns',
    message:
      'This step asks the model to do something mechanical — submit or copy a value it was already given. ' +
      'It may behave differently from one run to the next. Running it as code instead would make it repeatable.',
    raw: `advice: ${node.id} is an agent step doing deterministic work (ADR-080 D7)`,
    fixLabel: 'Make this step repeatable',
    fixPrompt:
      `The step "${node.id}" is an agent step, but its work is mechanical — it submits or copies a value it ` +
      `was already given, so it may behave differently from one run to the next. Change it to run as code ` +
      `instead, calling the same tool, and keep everything else in this workflow as it is.`,
  }
}

/**
 * adviseTypedInId flags a long id written into the workflow.
 *
 * D6's finding. The fix is deliberately NOT "replace it with a reference"
 * applied blindly — which reference is a question only the operator can answer,
 * and guessing would substitute one silent wrong id for another. It asks.
 */
export function adviseTypedInId(node: GraphNode): Finding | null {
  const text = textOf(node.spec)
  const m = LONG_ID.exec(text)
  if (!m) return null
  return {
    nodeId: node.id,
    severity: 'warns',
    message:
      `This step has an ID typed into it (${m[0]}). If that ID ever changes, nothing here will notice — ` +
      "the workflow will keep using the old one. Pointing at the fleet's shared reference data instead " +
      'means it stays correct on its own.',
    raw: `advice: ${node.id} contains the literal id ${m[0]}`,
    fixLabel: 'Use shared reference data',
    fixPrompt:
      `The step "${node.id}" has the ID ${m[0]} typed directly into it. Replace it with a reference to the ` +
      `fleet's shared reference data so it cannot go stale. If you cannot tell which reference is correct, ` +
      `say so and tell me what you would need — do not guess an ID.`,
  }
}

const RULES = [adviseAgentStep, adviseTypedInId]

/**
 * adviseNodes runs every advice rule over the graph.
 *
 * Advice is ADDITIVE to validator findings, never a replacement: a step can both
 * fail validation and be badly typed, and the operator needs the blocking one
 * first. Ordering is left to the caller's severity sort.
 */
export function adviseNodes(nodes: GraphNode[]): Finding[] {
  const out: Finding[] = []
  for (const n of nodes) {
    for (const rule of RULES) {
      const f = rule(n)
      if (f) out.push(f)
    }
  }
  return out
}
